<template>
  <div class="ProfileBilling__wrapper">
    <!-- Hammer for Mac Promotional Banner -->
    <div class="ProfileBilling__promo-banner" v-if="!user.isPaid">
      <div class="ProfileBilling__promo-content">
        <v-icon color="white" class="ProfileBilling__promo-icon">mdi-apple</v-icon>
        <div class="ProfileBilling__promo-text">
          <strong>Hammer for Mac</strong> – Get access to our highly acclaimed native Mac app for building static sites, bundled with every Forge paid plan.
          <a 
            href="https://hammerformac.com" 
            target="_blank" 
            rel="noopener noreferrer"
            class="ProfileBilling__promo-link"
          >
            Learn more →
          </a>
        </div>
      </div>
    </div>

    <div class="ProfileBilling" v-if="!user.isPaid && !enterClicked">
      <div class="ProfileBilling__card">
        <h2 class="ProfileBilling__title">Start Building with Forge</h2>
        <p class="ProfileBilling__subtitle">
          Get instant access to deploy, build, and scale your web applications
        </p>

        <div class="ProfileBilling__value-prop">
          <div class="ProfileBilling__value-item">
            <v-icon color="y1" class="ProfileBilling__value-icon">mdi-rocket-launch</v-icon>
            <div>
              <strong>5 Sites Included</strong>
              <p>Deploy multiple projects from day one</p>
            </div>
          </div>
          <div class="ProfileBilling__value-item">
            <v-icon color="y1" class="ProfileBilling__value-icon">mdi-speedometer</v-icon>
            <div>
              <strong>5GB Bandwidth</strong>
              <p>Fast global CDN included</p>
            </div>
          </div>
          <div class="ProfileBilling__value-item">
            <v-icon color="y1" class="ProfileBilling__value-icon">mdi-shield-check</v-icon>
            <div>
              <strong>All Features Unlocked</strong>
              <p>Custom domains, TurboJS, 24/7 support</p>
            </div>
          </div>
        </div>

        <div class="ProfileBilling__pricing">
          <span class="ProfileBilling__pricing-amount">Starting at $4/month</span>
          <span class="ProfileBilling__pricing-note">Cancel anytime, no commitments</span>
        </div>

        <v-btn 
          @click="enter" 
          color="y1" 
          class="ProfileBilling__btn" 
          dark
          large
          x-large
        >
          Choose Your Plan
        </v-btn>

        <p class="ProfileBilling__trust-note">
          Secure payment powered by Stripe • No hidden fees
        </p>
      </div>
    </div>
    <div v-else-if="user.isPaid">
      <PaidBilling
        :baseFeatures="baseFeatures"
        :plans="plans"
        :currentPlan="user.plan_id"
        :userId="user.id"
        :sitesCount="sites.length"
      />
    </div>

    <!-- Use Cases Section -->
    <div class="ProfileBilling__use-cases" v-if="!user.isPaid">
      <h2 class="ProfileBilling__use-cases-title">What You Can Build with Forge</h2>
      <div class="ProfileBilling__use-cases-grid">
        <div
          v-for="useCase in useCases"
          :key="useCase.title"
          class="ProfileBilling__use-case-card"
        >
          <v-icon :color="useCase.iconColor" class="ProfileBilling__use-case-icon">
            {{ useCase.icon }}
          </v-icon>
          <h3 class="ProfileBilling__use-case-title">{{ useCase.title }}</h3>
          <p class="ProfileBilling__use-case-description">{{ useCase.description }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { MODAL_ENTER_BILLING } from '@/components/Modals'
import { mapState } from 'vuex'
import PaidBilling from '@/components/TheProfile/PaidBilling'
export default {
  name: 'ProfileBilling',
  components: { PaidBilling },
  data() {
    return {
      enterClicked: false,
      baseFeatures: [
        'Custom Domains',
        'TurboJS Enabled',
        'Global CDN',
        '24/7 Support',
      ],
      plans: [
        {
          name: 'Lead',
          cost: '$4 / month',
          sitesCount: '5 sites',
          bandwidth: '5GB',
          extra: 25,
        },
        {
          name: 'Copper',
          cost: '$10 / month',
          sitesCount: '20 sites',
          bandwidth: '25GB',
          extra: 20,
        },
        {
          name: 'Bronze',
          cost: '$50 / month',
          sitesCount: 'Unlimited sites',
          bandwidth: '250GB',
          extra: 18,
        },
      ],
      useCases: [
        {
          title: 'Launch a Product Business',
          description: 'Build web apps, product marketing funnels, and SaaS platforms',
          icon: 'mdi-rocket-launch',
          iconColor: 'y1',
        },
        {
          title: 'Build Plugins',
          description: 'Create plugins for Forge, Miro, Attio, Shopify, and more',
          icon: 'mdi-puzzle',
          iconColor: 'y1',
        },
        {
          title: 'Build a Personal Brand',
          description: 'Create portfolio sites, CV websites, and personal projects',
          icon: 'mdi-account-star',
          iconColor: 'y1',
        },
        {
          title: 'Learn Web Development',
          description: 'Use Forge Academy, build test projects, and practice your skills',
          icon: 'mdi-school',
          iconColor: 'y1',
        },
      ],
    }
  },
  methods: {
    enter() {
      this.$store.commit('application/openModal', {
        component: MODAL_ENTER_BILLING,
        props: {
          baseFeatures: this.baseFeatures,
          plans: this.plans,
          currentPlan: this.user.plan_id,
          userId: this.user.id,
        },
      })
    },
  },
  computed: {
    ...mapState({
      sites: (state) => state.sites.list,
      user: (state) => state.user.current,
    }),
  },
}
</script>

<style lang="scss">
$style: ProfileBilling;
.#{$style}__wrapper {
  padding-top: 80px;
}
.#{$style}__promo-banner {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 6px;
  padding: 10px 16px;
  margin-bottom: 20px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}
.#{$style}__promo-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: white;
}
.#{$style}__promo-icon {
  flex-shrink: 0;
  font-size: 18px !important;
}
.#{$style}__promo-text {
  font-size: $H12;
  font-weight: 400;
  line-height: 1.4;
  color: white;
  text-align: center;
  display: inline-block;
  strong {
    font-weight: 600;
  }
}
.#{$style}__promo-link {
  color: white !important;
  font-weight: 500;
  text-decoration: underline;
  margin-left: 4px;
  white-space: nowrap;
  display: inline;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 0.9;
  }
  &:visited {
    color: white !important;
  }
}
.#{$style} {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  &__card {
    background: $N4;
    border: 1px solid $N5;
    border-radius: 8px;
    padding: 24px 20px;
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  &__title {
    font-weight: 600;
    font-size: 24px;
    color: $white;
    margin-bottom: 8px;
    text-align: center;
  }
  &__subtitle {
    font-size: $H14;
    font-weight: 300;
    color: $N2;
    text-align: center;
    margin-bottom: 20px;
    line-height: 1.4;
  }
  &__value-prop {
    width: 100%;
    max-width: 500px;
    margin: 0 auto 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  &__value-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    text-align: left;
    strong {
      display: block;
      color: $white;
      font-size: $H14;
      font-weight: 500;
      margin-bottom: 2px;
    }
    p {
      color: $N2;
      font-size: $H12;
      font-weight: 300;
      margin: 0;
      line-height: 1.3;
    }
  }
  &__value-icon {
    margin-top: 2px;
    flex-shrink: 0;
    font-size: 20px !important;
  }
  &__pricing {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 16px;
    gap: 2px;
  }
  &__pricing-amount {
    font-size: 20px;
    font-weight: 600;
    color: $white;
  }
  &__pricing-note {
    font-size: $H12;
    color: $N2;
    font-weight: 300;
  }
  &__btn {
    width: 100%;
    max-width: 300px;
    margin: 0 auto 12px;
    font-size: $H14;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0.5px;
    padding: 12px 24px !important;
    height: auto !important;
  }
  &__trust-note {
    font-size: $H11;
    color: $N3;
    text-align: center;
    margin: 0;
    font-weight: 300;
  }
  &__texts {
    text-align: center;
    p {
      font-size: $H14;
      font-weight: 300;
      span {
        font-weight: 500;
      }
    }
  }
  &__use-cases {
    margin-top: 32px;
    padding: 24px 16px;
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
  }
  &__use-cases-title {
    font-size: 20px;
    font-weight: 600;
    color: $white;
    text-align: center;
    margin-bottom: 20px;
  }
  &__use-cases-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    @media screen and (min-width: 768px) {
      grid-template-columns: repeat(4, 1fr);
    }
  }
  &__use-case-card {
    background: $N4;
    border: 1px solid $N5;
    border-radius: 8px;
    padding: 16px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    transition: all 0.3s ease;
    &:hover {
      border-color: $Y1;
      transform: translateY(-2px);
    }
  }
  &__use-case-icon {
    font-size: 32px !important;
    margin-bottom: 8px;
  }
  &__use-case-title {
    font-size: $H14;
    font-weight: 600;
    color: $white;
    margin-bottom: 6px;
    line-height: 1.3;
  }
  &__use-case-description {
    font-size: $H12;
    font-weight: 300;
    color: $N2;
    line-height: 1.4;
    margin: 0;
  }
}
</style>
